import { FunctionComponent, ReactNode, Ref, PropsWithoutRef, forwardRef } from 'react';
import styled, { css } from 'styled-components';

import Text from '../Text';
import Flex from '../Flex';
import { BaseProps, ForwardProps, NoChildrenProp } from '../../types';

export interface MetaListProps extends BaseProps, NoChildrenProp {
  /** Array of nodes or text to be rendered in the list. */
  items: ReactNode[];
  /**
   * If true, MetaList items that extend past the MetaList's container will wrap to a new line.
   * @default true
   */
  wrapItems?: boolean;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLUListElement>;
}

export const StyledMetaList = styled.ul<{ wrapItems: boolean }>(({ wrapItems }) => {
  return css`
    list-style: none;
    overflow: hidden;

    ${!wrapItems &&
    css`
      white-space: nowrap;
    `}
  `;
});

export const StyledMetaListItem = styled.li<{ wrapItems: boolean }>(({ wrapItems }) => {
  return css`
    min-width: 0;
    display: inline-block;

    ${wrapItems &&
    css`
      overflow-wrap: break-word;
    `}

    ${!wrapItems &&
    css`
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    `}
  `;
});

const MetaList: FunctionComponent<MetaListProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<MetaListProps>, ref: MetaListProps['ref']) => {
    const { items, wrapItems = true, ...restProps } = props;
    return (
      <Flex
        as={StyledMetaList}
        container={{ colGap: 0.5, alignItems: 'center', wrap: wrapItems ? 'wrap' : 'nowrap' }}
        wrapItems={wrapItems}
        ref={ref}
        {...restProps}
      >
        {items.flatMap((item, i, arr) =>
          item
            ? [
                <Text
                  as={StyledMetaListItem}
                  key={`${i + 0}`}
                  variant='secondary'
                  wrapItems={wrapItems}
                >
                  {item}
                </Text>
              ].concat(
                i !== arr.length - 1
                  ? [
                      <Text
                        as={StyledMetaListItem}
                        role='separator'
                        key={`${i + 0}-sep`}
                        variant='secondary'
                      >
                        •
                      </Text>
                    ]
                  : []
              )
            : []
        )}
      </Flex>
    );
  }
);

export default MetaList;
